import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MapPin, Wifi, WifiOff, Navigation, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function DriverTrack() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [coords, setCoords] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [pending, setPending] = useState(0);
  const [geoErr, setGeoErr] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const timerRef = useRef(null);

  const KEY = `arrow_track_${token}`;
  const getQueue = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
  const setQueue = (q) => localStorage.setItem(KEY, JSON.stringify(q));

  useEffect(() => {
    api.get(`/track-driver/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(apiErr(e)));
    setPending(getQueue().length);
    // eslint-disable-next-line
  }, [token]);

  const flush = async (extra) => {
    let q = getQueue();
    if (extra) q = [...q, extra];
    if (!q.length) return;
    try {
      await api.post(`/track-driver/${token}/ping`, { pings: q });
      setQueue([]); setPending(0); setLastSent(new Date());
    } catch {
      setQueue(q); setPending(q.length);
    }
  };

  const capture = () => {
    if (!navigator.geolocation) { setGeoErr("Geolocation is not supported on this device."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: new Date().toISOString() };
        setCoords(p); setGeoErr("");
        flush(p);
      },
      (err) => setGeoErr(err.message || "Unable to access location. Please allow GPS permission."),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  const start = () => {
    setSharing(true);
    capture();
    timerRef.current = setInterval(capture, 5 * 60 * 1000);
  };

  const stop = () => {
    setSharing(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [token]);

  if (error) return (
    <Shell>
      <div className="text-center py-16" data-testid="driver-error">
        <div className="text-2xl font-heading font-black">Link not found</div>
        <p className="text-muted-foreground mt-2">{error}</p>
      </div>
    </Shell>
  );
  if (!info) return <Shell><div className="py-20 text-center text-muted-foreground">Loading…</div></Shell>;

  return (
    <Shell>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary"><Navigation className="h-4 w-4" /> Driver Live Tracking</div>
        <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Order {info.order_number}</h1>
        <p className="text-muted-foreground text-sm mt-1 flex items-center gap-1"><MapPin className="h-4 w-4 text-primary" /> {info.pickup} → {info.delivery}</p>
      </div>

      <div className="border border-border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">{online ? <Wifi className="h-4 w-4 text-emerald-500" /> : <WifiOff className="h-4 w-4 text-amber-500" />}{online ? "Online" : "Offline — locations will send when reconnected"}</span>
          {pending > 0 && <span className="text-amber-500 font-mono-num text-xs">{pending} cached</span>}
        </div>

        {!sharing ? (
          <Button className="rounded-none w-full h-12 font-bold uppercase tracking-wide gap-2" onClick={start} data-testid="start-sharing-btn">
            <Navigation className="h-4 w-4" /> Start Sharing My Location
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 text-center" data-testid="sharing-active">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              <div className="font-bold uppercase tracking-wide text-sm">Sharing Active</div>
              <p className="text-xs text-muted-foreground mt-1">Your location syncs automatically every 5 minutes. Keep this page open.</p>
            </div>
            {coords && (
              <div className="text-xs text-muted-foreground font-mono-num text-center" data-testid="driver-coords">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                {lastSent && <span> · sent {lastSent.toLocaleTimeString()}</span>}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none flex-1" onClick={capture} data-testid="send-now-btn">Send Now</Button>
              <Button variant="ghost" className="rounded-none flex-1 text-destructive" onClick={stop} data-testid="stop-sharing-btn">Stop Sharing</Button>
            </div>
          </div>
        )}

        {geoErr && <p className="text-xs text-destructive text-center" data-testid="geo-error">{geoErr}</p>}
      </div>

      <p className="text-[11px] text-muted-foreground mt-4 text-center">Your location is shared only with Arrow Logistics for this delivery.</p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border">
        <div className="max-w-md mx-auto px-5 py-4"><Logo className="h-8" /></div>
      </div>
      <div className="max-w-md mx-auto px-5 py-10">{children}</div>
    </div>
  );
}
